/**
 * iter722 — Print Log (client-side ring buffer).
 *
 * Every receipt print attempt (ePOS-Direct, Bridge, KOT) writes one
 * structured row here so the user can later download / paste / share
 * the log when receipts mysteriously fail to come out of the printer.
 *
 * Why a ring buffer in localStorage?
 *   - Persists across page reloads (cashiers often refresh between
 *     orders, and we don't want to lose the "what just failed" context).
 *   - Capped to 250 rows so a chatty kitchen printer can't fill up the
 *     iPad's storage quota.
 *   - localStorage is per-origin, so multi-tenant browsers don't leak
 *     logs across tenants. (The cashier still has to manually share.)
 *
 * Each row is a JSON object:
 *   {
 *     ts:        ISO 8601 timestamp
 *     channel:   'epos-direct' | 'bridge' | 'kot' | string
 *     ok:        boolean
 *     printer:   { name, ip, deviceId, paper_width }
 *     receipt:   { order_number, total, items_count, title }
 *     result:    { error?, msg?, mode? }  ← whatever printViaEposDirect returned
 *     duration_ms: number
 *     ua:        navigator.userAgent (first 120 chars) — helps diagnose iOS vs Win
 *     href:      window.location.pathname so support can tell which screen fired it
 *   }
 *
 * Public API:
 *   - logPrintEvent(entry)         → push one row
 *   - readPrintLog()               → returns full array (oldest → newest)
 *   - clearPrintLog()              → wipes it
 *   - exportPrintLogText()         → human-readable plaintext for sharing
 */

const LS_KEY = 'smarto.print.log.v1';
const MAX_ROWS = 250;
// iter741 — Developer toggle: when ON, the raw Epson ePOS XML payload
// is captured into each log row's `result.xml` so the operator can
// inspect the exact bytes the printer received without Safari Web
// Inspector. localStorage key matches the rest of the printer feature
// family for consistency.
const CAPTURE_XML_KEY = 'smarto.print.capture_xml.v1';

export function isCaptureXmlEnabled() {
  try {
    return localStorage.getItem(CAPTURE_XML_KEY) === '1';
  } catch {
    return false;
  }
}

export function setCaptureXmlEnabled(on) {
  try {
    if (on) localStorage.setItem(CAPTURE_XML_KEY, '1');
    else localStorage.removeItem(CAPTURE_XML_KEY);
  } catch {
    // localStorage disabled — silently ignore.
  }
}

// iter745 — "Print receipt as image" (raster) mode. When ON, the whole
// receipt (logo + header + items + totals + footer) is rendered to a
// monochrome bitmap in the browser and sent as ONE ePOS <image> element.
// This makes Arabic print perfectly (browser handles RTL shaping) and
// sidesteps the printer's font limitations. Default ON because the
// user's TM-m30II can't print Arabic from <text>. They can flip it OFF
// from /printer-setup to fall back to text-only receipts.
const RASTER_MODE_KEY = 'smarto.print.raster_mode.v1';

export function isRasterModeEnabled() {
  try {
    const v = localStorage.getItem(RASTER_MODE_KEY);
    return v === null ? true : v === '1'; // default ON
  } catch {
    return true;
  }
}

export function setRasterModeEnabled(on) {
  try {
    localStorage.setItem(RASTER_MODE_KEY, on ? '1' : '0');
  } catch {
    // localStorage disabled — silently ignore.
  }
}

// iter753 — Print size (receipt scale). 'normal' = standard density,
// 'large' = fewer columns → bigger characters (easier to read). Drives the
// `cols` used for layout + raster font sizing in eposPrint.js.
const PRINT_SCALE_KEY = 'smarto.print.scale.v1';

export function getPrintScale() {
  try {
    const v = localStorage.getItem(PRINT_SCALE_KEY);
    return v === 'large' ? 'large' : 'normal';
  } catch {
    return 'normal';
  }
}

export function setPrintScale(scale) {
  try {
    localStorage.setItem(PRINT_SCALE_KEY, scale === 'large' ? 'large' : 'normal');
  } catch {
    // localStorage disabled — silently ignore.
  }
}

function _read() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function _write(arr) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(arr));
  } catch {
    // Storage full / quota / disabled — silently drop. We never want
    // the logger itself to break printing.
  }
}

export function logPrintEvent(entry) {
  if (typeof localStorage === 'undefined') return;
  try {
    const row = {
      ts: new Date().toISOString(),
      channel: entry?.channel || 'unknown',
      ok: !!entry?.ok,
      printer: {
        name: entry?.printer?.name || '',
        ip: entry?.printer?.ip || entry?.printer?.ip_address || '',
        deviceId: entry?.printer?.deviceId || entry?.printer?.epos_device_id || '',
        paper_width: entry?.printer?.paper_width || '',
      },
      receipt: {
        order_number: entry?.receipt?.order_number || entry?.receipt?.check_number || '',
        total: entry?.receipt?.total ?? null,
        items_count: Array.isArray(entry?.receipt?.items) ? entry.receipt.items.length : (entry?.receipt?.items_count ?? null),
        title: entry?.receipt?.title || '',
      },
      result: {
        error: entry?.result?.error || '',
        msg: entry?.result?.msg || '',
        mode: entry?.result?.mode || '',
        // iter727 — Per-receipt logo + QR rendering outcomes so users
        // can tell at a glance whether their logo or QR actually made
        // it onto the paper (or got skipped due to CORS / missing
        // URL / disabled toggle / older firmware quirk).
        logo: entry?.result?.logo || '',
        qr: entry?.result?.qr || '',
        // iter748 — Parsed Epson ePOS-Print response (success/code/status)
        // now that we read the printer's reply instead of firing blind.
        eposResult: entry?.result?.eposResult || '',
        // iter741 — Raw Epson ePOS XML payload (only present when the
        // "Capture XML to Print Log" developer toggle is ON). Lets the
        // operator paste the exact bytes the firmware received into a
        // bug report. Truncated at 64 KB just in case a future
        // megabyte-logo bug ever lands here.
        xml: typeof entry?.result?.xml === 'string' ? entry.result.xml.slice(0, 64 * 1024) : '',
      },
      duration_ms: typeof entry?.duration_ms === 'number' ? entry.duration_ms : null,
      ua: (typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '').slice(0, 120),
      href: (typeof window !== 'undefined' && window.location ? window.location.pathname : '') || '',
    };
    const arr = _read();
    arr.push(row);
    while (arr.length > MAX_ROWS) arr.shift();
    _write(arr);
  } catch {
    // never throw from the logger
  }
}

export function readPrintLog() {
  return _read();
}

export function clearPrintLog() {
  try { localStorage.removeItem(LS_KEY); } catch { /* noop */ }
}

/**
 * Build a human-friendly plaintext dump the user can copy into a
 * support email / WhatsApp message. Newest entries last.
 */
export function exportPrintLogText() {
  const rows = _read();
  if (!rows.length) return '(print log is empty — no print attempts recorded yet)';
  const header = [
    `Smarto Print Log — ${rows.length} row(s)`,
    `Exported: ${new Date().toISOString()}`,
    `Browser:  ${(typeof navigator !== 'undefined' && navigator.userAgent) || ''}`,
    `Origin:   ${(typeof window !== 'undefined' && window.location && window.location.origin) || ''}`,
    '-'.repeat(72),
  ].join('\n');
  const body = rows.map((r, i) => {
    const tag = r.ok ? 'OK' : 'FAIL';
    const printerLine = `[${r.printer?.name || '-'}] ${r.printer?.ip || '-'} devid="${r.printer?.deviceId || '-'}" paper=${r.printer?.paper_width || '-'}`;
    const receiptLine = `order#=${r.receipt?.order_number || '-'} total=${r.receipt?.total ?? '-'} items=${r.receipt?.items_count ?? '-'} title="${r.receipt?.title || '-'}"`;
    const resultLine = r.ok
      ? `mode=${r.result?.mode || '-'}`
      : `error=${r.result?.error || '-'} msg="${r.result?.msg || '-'}"`;
    // iter727 — Surface logo + QR rendering status on every row of
    // the exported text. Top failure modes are visible at a glance:
    //   • logo: no-url        → user didn't upload a thermal logo
    //   • logo: failed (CORS) → image URL doesn't return CORS headers
    //   • logo: rendered-cors (384x120) → printed fine
    //   • qr:   emitted       → QR XML went on the wire
    //   • qr:   disabled-in-settings → user has toggle off
    const renderLine = `logo=${r.result?.logo || '-'} | qr=${r.result?.qr || '-'}`;
    // iter741 — When XML capture was on for this row, append the raw
    // payload at the end so support can diff it against working
    // receipts.
    const xmlBlock = r.result?.xml
      ? `\n     ----- raw ePOS XML (${r.result.xml.length} chars) -----\n${r.result.xml}\n     ----- end raw XML -----`
      : '';
    return [
      `#${String(i + 1).padStart(3, '0')}  ${r.ts}  [${r.channel}] ${tag}  (${r.duration_ms ?? '-'}ms)`,
      `     printer: ${printerLine}`,
      `     receipt: ${receiptLine}`,
      `     result:  ${resultLine}`,
      `     render:  ${renderLine}`,
      `     screen:  ${r.href || '-'}${xmlBlock}`,
    ].join('\n');
  }).join('\n');
  return `${header}\n${body}\n`;
}
